import { ApiContext, ApiRequestError } from "@/hooks/api/base"
import { confirmChunk, presignChunk } from "@/hooks/api/meetings/live-recording-sessions/api"

import { markChunkUploaded } from "./recording-db"

const MAX_RETRIES = 3
const INITIAL_BACKOFF_MS = 1000

export type ChunkUploadResult = {
  success: boolean
  key?: string
  error?: string
  full?: boolean
  final?: boolean
}

export async function uploadChunk(
  ctx: ApiContext,
  meetingId: string,
  sessionId: string,
  chunkId: number,
  sequence: number,
  blob: Blob,
  contentType: string
): Promise<ChunkUploadResult> {
  let lastError: Error | null = null

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const { url, key, final: isFinal } = await presignChunk(ctx, meetingId, sessionId, sequence, contentType)

      const response = await fetch(url, {
        method: "PUT",
        signal: AbortSignal.timeout(60_000),
        body: blob,
        headers: {
          "Content-Type": contentType,
        },
      })

      if (!response.ok) {
        throw new Error(`S3 upload failed with status ${response.status}`)
      }

      await confirmChunk(ctx, meetingId, sessionId, sequence, key)

      await markChunkUploaded(chunkId, key)

      return { success: true, key, final: isFinal }
    } catch (error) {
      if (error instanceof ApiRequestError && error.message === "recording_full") {
        return { success: false, full: true }
      }

      lastError = error instanceof Error ? error : new Error(String(error))

      if (attempt < MAX_RETRIES - 1) {
        const backoff = INITIAL_BACKOFF_MS * Math.pow(2, attempt)
        await sleep(backoff)
      }
    }
  }

  return {
    success: false,
    error: lastError?.message || "Unknown error",
  }
}

export type BatchUploadProgress = {
  total: number
  completed: number
  failed: number
}

export type BatchUploadResult = {
  success: boolean
  uploadedCount: number
  failedCount: number
  failedSequences: number[]
}

export async function uploadChunkBatch(
  ctx: ApiContext,
  meetingId: string,
  sessionId: string,
  chunks: { id: number; sequence: number; blob: Blob }[],
  contentType: string,
  onProgress?: (progress: BatchUploadProgress) => void
): Promise<BatchUploadResult> {
  const total = chunks.length
  let completed = 0
  let failed = 0
  const failedSequences: number[] = []

  for (const chunk of chunks) {
    const result = await uploadChunk(ctx, meetingId, sessionId, chunk.id, chunk.sequence, chunk.blob, contentType)

    if (result.full) {
      failed++
      failedSequences.push(chunk.sequence)
    } else if (result.success) {
      completed++
    } else {
      failed++
      failedSequences.push(chunk.sequence)
    }

    onProgress?.({ total, completed, failed })
  }

  return {
    success: failed === 0,
    uploadedCount: completed,
    failedCount: failed,
    failedSequences,
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
